import type { LocationRewrite } from "@tanstack/react-router"

// nightshift.am serves Terminal at its root without redirecting: the router
// reads nightshift.am/<path> as /terminal/<path> and writes /terminal links
// back without the prefix. Server routes and the auth callback pass through
// unchanged.
const NIGHTSHIFT_HOSTS = new Set(["nightshift.am", "www.nightshift.am"])
const WATTBOT_HOSTS = new Set(["wattbot.com", "www.wattbot.com"])

// Where a link that leaves the app (an export, a definition's example) opens.
export const NIGHTSHIFT_ORIGIN = "https://nightshift.am"
const PASSTHROUGH_PATH =
  /^\/(terminal|api|_serverFn|robots\.txt|sitemap\.xml)(\/|$)/
const TERMINAL_PREFIX = "/terminal"
const TERMINAL_PATH = /^\/terminal(\/|$)/
// A sign-in link keeps its PKCE verifier cookie on the host that sent it, so
// the callback answers on that host, even an old wattbot.com link.
const AUTH_CALLBACK_PATH = "/terminal/auth/callback"

export const nightshiftRewrite: LocationRewrite = {
  input: ({ url }) => {
    if (!NIGHTSHIFT_HOSTS.has(url.hostname)) return undefined
    if (PASSTHROUGH_PATH.test(url.pathname)) return undefined
    url.pathname =
      url.pathname === "/"
        ? TERMINAL_PREFIX
        : `${TERMINAL_PREFIX}${url.pathname}`
    return url
  },
  output: ({ url }) => {
    if (!NIGHTSHIFT_HOSTS.has(url.hostname)) return undefined
    if (!TERMINAL_PATH.test(url.pathname)) return undefined
    url.pathname = url.pathname.slice(TERMINAL_PREFIX.length) || "/"
    return url
  },
}

// Each Terminal page has one public URL: wattbot.com/terminal/<path> and
// nightshift.am/terminal/<path> both move to nightshift.am/<path>.
export const getNightshiftRedirect = (url: URL): string | undefined => {
  const isTerminalHost =
    WATTBOT_HOSTS.has(url.hostname) || NIGHTSHIFT_HOSTS.has(url.hostname)
  if (!isTerminalHost) return undefined
  if (!TERMINAL_PATH.test(url.pathname)) return undefined
  if (url.pathname === AUTH_CALLBACK_PATH) return undefined
  const path = url.pathname.slice(TERMINAL_PREFIX.length) || "/"
  return `${NIGHTSHIFT_ORIGIN}${path}${url.search}`
}
